import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Calendar, Tag } from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import { POSTS, BLOG_PATH } from "../data/blog";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogIndex() {
  const canonical = `${SITE_URL}/blog`;

  useDocumentMeta({
    title:
      "Auto Electrical Blog | Diagnostics, A/C & DIY Help | Powerz Auto Electrical Sunshine Coast",
    description:
      "Sunshine Coast auto electrical blog — straight-talking guides on diagnostics, A/C, dual battery and DIY 4WD electrical work from 10+ years at the bench. Powerz Auto Electrical, Kunda Park.",
    canonical,
    image: SITE.business.logo,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Powerz Auto Electrical Blog",
        url: canonical,
        publisher: {
          "@type": "Organization",
          name: SITE.business.name,
          logo: { "@type": "ImageObject", url: `${SITE_URL}${SITE.business.logo}` },
        },
        blogPost: POSTS.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          description: p.metaDesc,
          datePublished: p.publishedAt,
          author: { "@type": "Organization", name: SITE.business.name },
          image: `${SITE_URL}${p.image}`,
          url: `${SITE_URL}${BLOG_PATH(p.slug)}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: canonical },
        ],
      },
    ],
  });

  const [featured, ...rest] = POSTS;

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-black pt-40 md:pt-48 pb-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              FROM THE WORKSHOP
            </span>
          </div>
          <h1
            data-testid="blog-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight max-w-5xl"
          >
            POWERZ <span className="text-cyan-pz glow-cyan">BLOG.</span>
          </h1>
          <p className="mt-6 text-white/70 barlow text-base md:text-lg max-w-3xl">
            Straight-talking guides from 10+ years at the bench — diagnostics, A/C,
            dual battery work and the DIY traps to avoid. Written by the auto
            electricians actually swinging the multimeter at our Kunda Park workshop.
          </p>
        </div>
      </section>

      {/* Featured post */}
      <section className="relative bg-black pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Link
            to={BLOG_PATH(featured.slug)}
            data-testid="blog-featured-card"
            className="group block relative bg-[#0A0A0A] border border-white/10 overflow-hidden clip-perf hover:border-cyan-pz/50 transition-colors"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  loading="eager"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-cyan-pz text-black px-3 py-1.5 bebas tracking-widest text-xs">
                  FEATURED
                </span>
              </div>
              <div className="lg:col-span-5 p-7 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-white/50 barlow text-xs mb-4">
                  <Tag className="w-3.5 h-3.5 text-cyan-pz" />
                  <span>{featured.category}</span>
                  <span className="text-white/20">·</span>
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatDate(featured.publishedAt)}</span>
                  <span className="text-white/20">·</span>
                  <Clock className="w-3.5 h-3.5" />
                  <span>{featured.readTime}</span>
                </div>
                <h2 className="bebas text-white text-3xl md:text-4xl leading-[1] tracking-tight mb-4 group-hover:text-cyan-pz transition-colors">
                  {featured.title}
                </h2>
                <p className="text-white/70 barlow text-base leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 text-cyan-pz bebas tracking-widest text-sm">
                  READ ARTICLE
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Post grid */}
      <section className="relative bg-black pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post, idx) => (
              <Link
                key={post.slug}
                to={BLOG_PATH(post.slug)}
                data-testid={`blog-card-${idx}`}
                className="group flex flex-col bg-[#0A0A0A] border border-white/10 overflow-hidden clip-perf hover:border-cyan-pz/50 transition-colors"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-cyan-pz/40 text-cyan-pz px-2.5 py-1 bebas tracking-widest text-[10px]">
                    {post.category.toUpperCase()}
                  </span>
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-white/40 barlow text-[11px] mb-3">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(post.publishedAt)}</span>
                    <span className="text-white/20">·</span>
                    <Clock className="w-3 h-3" />
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="bebas text-white text-xl md:text-2xl leading-[1.05] tracking-tight mb-3 group-hover:text-cyan-pz transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-white/60 barlow text-sm leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-cyan-pz bebas tracking-widest text-xs">
                    READ MORE
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
